/**
 * Reusable caching factory for API requests with TTL support and request deduplication
 * Provides a unified caching solution across the entire application
 */

class ApiCache {
  // 5 minutes default
  constructor(name, defaultTTL = 5 * 60 * 1000) {
    this.name = name;
    this.cache = new Map();
    this.cacheTimestamps = new Map();
    this.cacheTTLs = new Map();
    this.ongoingRequests = new Map();
    this.defaultTTL = defaultTTL;
    this.cleanupHandle = null;

    if (typeof setInterval === 'function') {
      const interval = Math.max(this.defaultTTL, 60 * 1000);
      this.cleanupHandle = setInterval(() => this.pruneExpiredEntries(), interval);
    }
  }

  /**
   * Generate a cache key from parameters
   */
  generateCacheKey(params = {}) {
    // Sort params to ensure consistent key generation
    const sortedParams = Object.keys(params)
      .sort()
      .reduce((result, key) => {
        result[key] = params[key];
        return result;
      }, {});

    return JSON.stringify(sortedParams);
  }

  /**
   * Check if cached data is still valid
   */
  _isCacheValid(cacheKey, customTTL) {
    const timestamp = this.cacheTimestamps.get(cacheKey);
    if (!timestamp) return false;

    const ttl = customTTL ?? this.cacheTTLs.get(cacheKey) ?? this.defaultTTL;
    return Date.now() - timestamp < ttl;
  }

  /**
   * Get data with caching and deduplication
   */
  async get(fetchFunction, params = {}, options = {}) {
    if (typeof fetchFunction !== 'function') {
      throw new Error('fetchFunction must be a function');
    }

    const { ttl, forceRefresh = false, cacheFailures = false, onError } = options;
    const cacheKey = this.generateCacheKey(params);
    const requestedTtl = ttl ?? null;

    // Return cached data if available, valid, and not forcing refresh
    if (!forceRefresh && this.cache.has(cacheKey) && this._isCacheValid(cacheKey, requestedTtl)) {
      return this.cache.get(cacheKey);
    }

    // Clear expired cache entry
    if (this.cache.has(cacheKey) && !this._isCacheValid(cacheKey, requestedTtl)) {
      this._evictEntry(cacheKey);
    }

    // Return ongoing request if one exists
    if (this.ongoingRequests.has(cacheKey)) {
      return this.ongoingRequests.get(cacheKey);
    }

    // Make new request
    const requestPromise = (async () => {
      try {
        const result = await fetchFunction(params);
        this.cache.set(cacheKey, result);
        this.cacheTimestamps.set(cacheKey, Date.now());
        this.cacheTTLs.set(cacheKey, requestedTtl ?? this.defaultTTL);
        return result;
      } catch (error) {
        if (!cacheFailures) {
          this._evictEntry(cacheKey);
        } else {
          this.cache.set(cacheKey, { error, isError: true });
          this.cacheTimestamps.set(cacheKey, Date.now());
          this.cacheTTLs.set(cacheKey, requestedTtl ?? this.defaultTTL);
        }

        if (typeof onError === 'function') {
          try {
            onError(error);
          } catch (handlerError) {
            console.error('[ApiCache] Error handler threw an exception', handlerError); // eslint-disable-line no-console
          }
        }

        throw error;
      } finally {
        this.ongoingRequests.delete(cacheKey);
      }
    })();

    this.ongoingRequests.set(cacheKey, requestPromise);

    return requestPromise;
  }

  /**
   * Get cached data without making API call (respects TTL)
   */
  getCached(params = {}, customTTL) {
    const cacheKey = this.generateCacheKey(params);

    if (this.cache.has(cacheKey) && this._isCacheValid(cacheKey, customTTL)) {
      return this.cache.get(cacheKey);
    }

    // Clear expired cache entry
    if (this.cache.has(cacheKey)) {
      this._evictEntry(cacheKey);
    }

    return null;
  }

  /**
   * Check if data is cached and still valid
   */
  hasCached(params = {}, customTTL) {
    const cacheKey = this.generateCacheKey(params);
    return this.cache.has(cacheKey) && this._isCacheValid(cacheKey, customTTL);
  }

  /**
   * Force refresh data (clears cache first)
   */
  async refresh(fetchFunction, params = {}, options = {}) {
    return this.get(fetchFunction, params, { ...options, forceRefresh: true });
  }

  /**
   * Clear all cache entries
   */
  clearAll() {
    this.cache.clear();
    this.cacheTimestamps.clear();
    this.cacheTTLs.clear();
    this.ongoingRequests.clear();
  }

  /**
   * Clear specific cache entry
   */
  clearEntry(params = {}) {
    const cacheKey = this.generateCacheKey(params);
    this._evictEntry(cacheKey);
    this.ongoingRequests.delete(cacheKey);
  }

  /**
   * Set cache data optimistically (useful for immediate UI updates before API confirmation)
   * @param {*} data - The data to cache
   * @param {Object} params - Cache key parameters
   * @param {Object} options - Options for the cache entry
   * @param {number} options.ttl - Custom TTL for this entry
   */
  setOptimistic(data, params = {}, options = {}) {
    const cacheKey = this.generateCacheKey(params);
    const { ttl } = options;

    this.cache.set(cacheKey, data);
    this.cacheTimestamps.set(cacheKey, Date.now());
    this.cacheTTLs.set(cacheKey, ttl ?? this.defaultTTL);
  }

  /**
   * Get cache statistics
   */
  getStats() {
    return {
      name: this.name,
      entries: this.cache.size,
      ongoingRequests: this.ongoingRequests.size,
      defaultTTL: this.defaultTTL,
    };
  }

  /**
   * Dispose of timers and cached state
   */
  dispose() {
    if (this.cleanupHandle) {
      clearInterval(this.cleanupHandle);
      this.cleanupHandle = null;
    }

    this.clearAll();
  }

  pruneExpiredEntries() {
    const now = Date.now();

    this.cacheTimestamps.forEach((timestamp, cacheKey) => {
      const ttl = this.cacheTTLs.get(cacheKey) ?? this.defaultTTL;
      if (now - timestamp >= ttl) {
        this._evictEntry(cacheKey);
      }
    });
  }

  _evictEntry(cacheKey) {
    this.cache.delete(cacheKey);
    this.cacheTimestamps.delete(cacheKey);
    this.cacheTTLs.delete(cacheKey);
  }
}

/**
 * Cache Factory - creates and manages cache instances
 */
class CacheFactory {
  constructor() {
    this.caches = new Map();
  }

  /**
   * Create or get a cache instance
   */
  getCache(name, defaultTTL = 5 * 60 * 1000) {
    if (!this.caches.has(name)) {
      this.caches.set(name, new ApiCache(name, defaultTTL));
    }
    return this.caches.get(name);
  }

  /**
   * Clear all caches (useful for sign out)
   */
  clearAllCaches() {
    this.caches.forEach((cache) => cache.dispose());
  }

  /**
   * Remove a specific cache
   */
  removeCache(name) {
    const cache = this.caches.get(name);
    if (cache) {
      cache.dispose();
      this.caches.delete(name);
    }
  }

  /**
   * Get statistics for all caches
   */
  getAllStats() {
    const stats = {};
    this.caches.forEach((cache, name) => {
      stats[name] = cache.getStats();
    });
    return stats;
  }

  /**
   * Get list of all cache names
   */
  getCacheNames() {
    return Array.from(this.caches.keys());
  }
}

// Export singleton instance
export const cacheFactory = new CacheFactory();
export default cacheFactory;
